/**
 * Instellingenblok pushmeldingen (ronde E) — per apparaat.
 *
 * Het abonnement hoort bij déze browser op dít apparaat (het
 * push-endpoint is apparaatgebonden), dus de hoofdschakelaar en de
 * vier soort-schakelaars gelden alleen hier. Op iPhone/iPad werkt web
 * push pas vanaf iOS 16.4 én alleen wanneer het portaal als app op het
 * beginscherm staat — het blok legt dat ter plekke uit
 * (docs/PUSHMELDINGEN.md heeft het stappenplan).
 */

import { useCallback, useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { BellRing, Loader2 } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { pushApi, vertaalApiFout } from "@/lib/api";
import {
  abonneerBrowser,
  draaitStandalone,
  huidigBrowserAbonnement,
  isIos,
  naarAbonnementInvoer,
  pushWordtOndersteund,
} from "@/lib/push";
import type {
  PushAbonnementAntwoord,
  PushMeldingSoort,
  PushTestAntwoord,
} from "@/types/api";

const SOORTEN: PushMeldingSoort[] = [
  "chat_nieuw",
  "chat_overdracht",
  "chat_bericht",
  "mail_actie",
  "storing",
  "leerronde",
  "inkoop",
  "klant_wacht",
];

export function PushMeldingen() {
  const { t } = useTranslation();
  const ondersteund = pushWordtOndersteund();

  const configQuery = useQuery({
    queryKey: ["push", "config"],
    queryFn: pushApi.config,
    enabled: ondersteund,
    staleTime: 5 * 60 * 1000,
  });

  // De stand van dít apparaat: browser-abonnement + serverkant.
  const [abonnement, setAbonnement] = useState<PushAbonnementAntwoord | null>(
    null,
  );
  const [laden, setLaden] = useState(true);
  const [bezig, setBezig] = useState(false);

  useEffect(() => {
    let actueel = true;
    (async () => {
      if (!ondersteund) {
        setLaden(false);
        return;
      }
      try {
        const browserAbo = await huidigBrowserAbonnement();
        if (!browserAbo) return;
        const server = await pushApi.opvragen(browserAbo.endpoint);
        if (actueel) setAbonnement(server);
      } catch {
        /* stil: blok toont dan gewoon "uit" */
      } finally {
        if (actueel) setLaden(false);
      }
    })();
    return () => {
      actueel = false;
    };
  }, [ondersteund]);

  const zetAan = useCallback(async () => {
    const sleutel = configQuery.data?.publieke_sleutel;
    if (!sleutel) return;
    setBezig(true);
    try {
      const browserAbo = await abonneerBrowser(sleutel);
      const server = await pushApi.abonneer(
        naarAbonnementInvoer(browserAbo, {}),
      );
      setAbonnement(server);
      toast.success(t("profiel.push_aangezet"));
    } catch (fout) {
      if (fout instanceof Error && fout.message === "permissie_geweigerd") {
        toast.error(t("profiel.push_permissie_geweigerd"));
      } else if (
        fout instanceof Error &&
        ["AbortError", "NotAllowedError", "NotSupportedError"].includes(
          fout.name,
        )
      ) {
        // De browser kon niet bij zijn pushdienst (bv. geen verbinding,
        // of push door beleid uit) — geen rauwe Engelse foutmelding.
        toast.error(t("profiel.push_abonneren_mislukt"));
      } else {
        toast.error(vertaalApiFout(fout));
      }
    } finally {
      setBezig(false);
    }
  }, [configQuery.data?.publieke_sleutel, t]);

  const zetUit = useCallback(async () => {
    setBezig(true);
    try {
      const browserAbo = await huidigBrowserAbonnement();
      if (browserAbo) {
        await pushApi.afmelden(browserAbo.endpoint).catch(() => undefined);
        await browserAbo.unsubscribe();
      }
      setAbonnement(null);
      toast.success(t("profiel.push_uitgezet"));
    } catch (fout) {
      toast.error(vertaalApiFout(fout));
    } finally {
      setBezig(false);
    }
  }, [t]);

  const zetSoort = useCallback(
    async (soort: PushMeldingSoort, aan: boolean) => {
      const browserAbo = await huidigBrowserAbonnement();
      if (!browserAbo || !abonnement) return;
      const voorkeuren = { ...abonnement.voorkeuren, [soort]: aan };
      // Optimistisch: de schakelaar reageert direct.
      setAbonnement({ ...abonnement, voorkeuren });
      try {
        const server = await pushApi.abonneer(
          naarAbonnementInvoer(browserAbo, voorkeuren),
        );
        setAbonnement(server);
      } catch (fout) {
        setAbonnement(abonnement);
        toast.error(vertaalApiFout(fout));
      }
    },
    [abonnement],
  );

  // Testmelding (draaiboekronde 20-08): naar álle apparaten van dit
  // account, zodat JP zonder hulp kan checken of push echt werkt.
  const [testResultaat, setTestResultaat] = useState<PushTestAntwoord | null>(
    null,
  );
  const testMutatie = useMutation({
    // Zonder id: naar álle apparaten in één keer. Dat is de knop die
    // in het log één regel met het totale aantal apparaten oplevert.
    mutationFn: () => pushApi.testmelding(),
    onSuccess: (resultaat) => setTestResultaat(resultaat),
    onError: (fout) => toast.error(vertaalApiFout(fout)),
  });

  const actief = abonnement !== null;
  const iosZonderApp = isIos() && !draaitStandalone();

  return (
    <Card>
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-brand-orange-50 text-brand-orange-700 grid place-items-center">
            <BellRing className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h2 className="text-[15px] font-semibold">
              {t("profiel.push_titel")}
            </h2>
            <p className="text-[13px] text-muted-foreground mt-0.5">
              {t("profiel.push_uitleg")}
            </p>
          </div>
          {ondersteund && configQuery.data?.actief && (
            <div className="flex items-center gap-2">
              {(bezig || laden) && (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              )}
              <Switch
                checked={actief}
                disabled={bezig || laden}
                onCheckedChange={(aan) => (aan ? zetAan() : zetUit())}
                aria-label={t("profiel.push_titel")}
              />
            </div>
          )}
        </div>

        {!ondersteund ? (
          <p className="text-[13px] text-muted-foreground">
            {iosZonderApp
              ? t("profiel.push_ios_beginscherm")
              : t("profiel.push_niet_ondersteund")}
          </p>
        ) : configQuery.data && !configQuery.data.actief ? (
          <p className="text-[13px] text-muted-foreground">
            {t("profiel.push_niet_ingericht")}
          </p>
        ) : (
          <>
            {iosZonderApp && (
              <p className="text-[13px] text-muted-foreground">
                {t("profiel.push_ios_beginscherm")}
              </p>
            )}
            {actief && (
              <div className="space-y-2.5">
                {SOORTEN.map((soort) => (
                  <div
                    key={soort}
                    className="flex items-center justify-between gap-4"
                  >
                    <Label
                      htmlFor={`push-${soort}`}
                      className="text-[13.5px] font-normal"
                    >
                      {t(`profiel.push_soort_${soort}`)}
                    </Label>
                    <Switch
                      id={`push-${soort}`}
                      checked={abonnement.voorkeuren[soort] !== false}
                      onCheckedChange={(aan) => zetSoort(soort, aan)}
                    />
                  </div>
                ))}
                <p className="text-[12.5px] text-muted-foreground pt-1">
                  {t("profiel.push_per_apparaat", {
                    apparaat: abonnement.apparaat ?? "",
                  })}
                </p>
              </div>
            )}

            {/* Testmelding naar alle apparaten van dit account. */}
            <div className="border-t pt-4 space-y-2">
              <div className="flex items-center justify-between gap-4">
                <p className="text-[13px] text-muted-foreground">
                  {t("profiel.push_test_uitleg")}
                </p>
                <Button
                  variant="outline"
                  onClick={() => testMutatie.mutate()}
                  disabled={testMutatie.isPending}
                >
                  {testMutatie.isPending && (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  )}
                  {t("profiel.push_test_knop")}
                </Button>
              </div>
              {testResultaat && (
                <p
                  className="text-[13px] font-medium"
                  data-testid="push-test-resultaat"
                >
                  {testResultaat.totaal === 0
                    ? t("profiel.push_test_geen_abonnementen")
                    : t("profiel.push_test_resultaat", {
                        count: testResultaat.totaal,
                        geslaagd: testResultaat.geslaagd,
                        verlopen: testResultaat.verlopen,
                      })}
                </p>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
